// normalize.js
// The channel match key, in one place. Pure — no I/O, no React, no Graph.
//
// These rules are not new: they are the ones scripts/import_people.js used to
// write all 1,909 live Normalized values. The app must produce byte-identical
// output for the same input or the two halves of the system stop agreeing about
// who is who — Normalized is what duplicate detection, household warnings and
// (eventually) Watch sender recognition all key on. Parity against the real
// import output is asserted by a throwaway script, not assumed.
//
// Normalized is ALWAYS recomputed from Value. It is never typed, never edited,
// and never trusted from a form.

/** Email → trimmed lowercase. */
export function normalizeEmail(value) {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * Phone → digits only, with a leading US 1 stripped when the result is 11
 * digits. Everything else keeps every digit it has: an international number is
 * not ours to reinterpret, and a 3-digit extension is better matched exactly
 * than guessed at.
 */
export function normalizePhone(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return digits;
}

/** Dispatch on channel Kind, matching the two SharePoint choice values. */
export function normalizeValue(kind, value) {
  return kind === 'Email' ? normalizeEmail(value) : normalizePhone(value);
}

// A single deliverable-looking address: one @, no whitespace, a dotted domain.
// Deliberately shape-only — it exists to reject prose typed into an email field
// (the import found 13 such entries), not to implement RFC 5322.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** True when the value is usable as an email channel. */
export function isValidEmail(value) {
  return EMAIL_SHAPE.test(String(value ?? '').trim());
}

/**
 * True when the value is usable as a phone channel. Under 7 digits is an
 * extension or a fragment, not a reachable number — the import routed 51 of
 * those to Notes rather than letting them pollute the match key, and the app
 * holds the same line.
 */
export function isValidPhone(value) {
  return normalizePhone(value).length >= 7;
}

export function isValidValue(kind, value) {
  return kind === 'Email' ? isValidEmail(value) : isValidPhone(value);
}

/**
 * Display formatting for US 10-digit numbers: (727) 547-8367.
 * DISPLAY ONLY — the stored Value is never rewritten by this.
 * Anything that is not exactly 10 digits after normalization is shown as
 * entered, because reformatting an international or partial number would be
 * inventing structure that is not there.
 */
export function formatPhoneForDisplay(value) {
  const raw = String(value ?? '');
  const d = normalizePhone(raw);
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Accepts YYYY-MM-DD or --MM-DD. Returns null when neither. */
export function parseBirthday(value) {
  const v = String(value ?? '').trim();
  if (!v) return null;
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m) {
    const [, y, mo, d] = m;
    if (+mo < 1 || +mo > 12 || +d < 1 || +d > 31) return null;
    return { year: +y, month: +mo, day: +d };
  }
  // Apple's year-less form. The import preserved 3 of these rather than
  // inventing a year, and a date column could not have held them at all.
  m = /^--(\d{2})-(\d{2})$/.exec(v);
  if (m) {
    const [, mo, d] = m;
    if (+mo < 1 || +mo > 12 || +d < 1 || +d > 31) return null;
    return { year: null, month: +mo, day: +d };
  }
  return null;
}

export function isValidBirthday(value) {
  const v = String(value ?? '').trim();
  return v === '' || parseBirthday(v) !== null;
}

/** "Mar 15, 1970" / "Mar 15". Unparseable input is shown as-is. */
export function formatBirthday(value) {
  const p = parseBirthday(value);
  if (!p) return String(value ?? '');
  const md = `${MONTHS[p.month - 1]} ${p.day}`;
  return p.year === null ? md : `${md}, ${p.year}`;
}
